import api from './api';
import { getContentList } from './content';
import { ContentStatus } from '../types/content';
import { UserDetailResponse, UserListResponse } from '../types/user';

// ============================================================================
// Website app-demo curation — talks to the shared backend (same api client
// as everything else: APIM base + subscription key + admin bearer token).
//
// Config lives in the backend's SiteConfig table (key 'app-demo-content'):
//   read  → GET /storage/content/site-demo            (public)
//   write → PUT /storage/admin/site-demo              (AdminAuthGuard)
// People search + per-person content browse use the ADMIN service — the same
// endpoints the Users section uses (GET /admin/users, /admin/users/:id,
// /admin/users/:id/content). Deliberately NOT the public users API: its APIM
// CORS origin-list doesn't include admin.xuman.ai, while the admin API's
// does — the dashboard should only ever call admin-guarded APIs anyway.
// The xuman.ai homepage renders the same GET /storage/content/site-demo feed.
// ============================================================================

export type DemoContentMode = 'default' | 'custom';

/**
 * 'manual' = admin-assigned positions; 'xuman_rating' = the backend computes
 * the play order from owners' CURRENT xumanRating (highest first) every time
 * the site loads the feed — the panel only previews it.
 */
export type DemoOrderBy = 'manual' | 'xuman_rating';

export const DEMO_MAX_ITEMS = 50;

export interface DemoContentPerson {
  userId: number;
  name: string;
  avatarUrl?: string | null;
}

export interface DemoContentItem {
  contentId: string;
  userId: number;
  /** Global play order across ALL people, 1..50 — 1 plays first. */
  position: number;
}

export interface DemoContentConfig {
  mode: DemoContentMode;
  orderBy: DemoOrderBy;
  people: DemoContentPerson[];
  items: DemoContentItem[];
  updatedAt: string;
  updatedBy?: string;
}

export interface PersonSuggestion {
  type: 'provider' | 'service';
  id: number;
  name: string;
  subtitle?: string;
  avatarUrl?: string;
  rating?: number | null;
}

export interface DemoBrowseItem {
  id: string;
  type: string;
  posterUrl: string;
  caption: string;
  serviceTag?: string | null;
}

export interface DemoPersonContent {
  person: {
    userId: number;
    name: string;
    avatarUrl: string | null;
    /** Current Xuman rating — used to preview the auto order. */
    xumanRating: number | null;
  };
  items: DemoBrowseItem[];
}

export const SITE_URL = 'https://xuman.ai';

// ---- Calls ----

export const getDemoConfig = async (): Promise<DemoContentConfig> => {
  const response = await api.get<{
    success: boolean;
    config: DemoContentConfig;
  }>('/storage/content/site-demo');
  const config = response.data.config;
  const items = (Array.isArray(config?.items) ? config.items : [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((it, index) => ({ ...it, position: index + 1 }));
  return {
    mode: config?.mode === 'custom' ? 'custom' : 'default',
    orderBy: config?.orderBy === 'xuman_rating' ? 'xuman_rating' : 'manual',
    people: Array.isArray(config?.people) ? config.people : [],
    items,
    updatedAt: config?.updatedAt || '',
    updatedBy: config?.updatedBy,
  };
};

export const saveDemoConfig = async (
  config: Pick<DemoContentConfig, 'mode' | 'orderBy' | 'people' | 'items'>
): Promise<DemoContentConfig> => {
  const response = await api.put<{
    success: boolean;
    config: DemoContentConfig;
  }>('/storage/admin/site-demo', config);
  return response.data.config;
};

export const searchDemoPeople = async (
  query: string
): Promise<PersonSuggestion[]> => {
  const response = await api.get<UserListResponse>('/admin/users', {
    params: { search: query, isProvider: 'true', limit: 10 },
  });
  return (response.data.data?.items || []).map((u) => ({
    type: 'provider' as const,
    id: u.id,
    name: u.name || u.email || `User ${u.id}`,
    subtitle: u.email || u.phone || undefined,
    avatarUrl: u.profileImage || undefined,
    rating: u.rating,
  }));
};

export const getDemoPersonContent = async (
  userId: number
): Promise<DemoPersonContent> => {
  // Content comes from the Content Review endpoint (/storage/admin/content):
  // unlike /admin/users/:id/content it returns SIGNED posterUrl for
  // blob-backed posters (AI content), not just external ones.
  const [detailRes, contentList] = await Promise.all([
    api.get<UserDetailResponse>(`/admin/users/${userId}`),
    getContentList({ ownerId: userId, status: ContentStatus.READY, limit: 100 }),
  ]);
  const user = detailRes.data.data;
  return {
    person: {
      userId: user.id,
      name: user.name || user.email || `User ${user.id}`,
      avatarUrl: user.profileImage || null,
      // The admin API doesn't expose xumanRating — the panel's rating-order
      // preview falls back to manual order; the SITE's order is computed
      // server-side from live ratings either way (GET /content/site-demo).
      xumanRating: null,
    },
    items: (contentList.data?.items || [])
      .filter((a) => a.status === 'READY' && a.posterUrl)
      .map((a) => ({
        id: a.id,
        type: a.type,
        posterUrl: a.posterUrl as string,
        caption: a.caption || '',
        serviceTag: a.serviceTag,
      })),
  };
};
